// Shared bulk-action orchestration for CustomersPage/CatalogPage/UsersPage
// (customers/products/users multi-select task). Plain async functions, not
// hooks — each page still owns its own react-query mutation hooks (cache
// invalidation stays page-local, same as every other mutation in this
// codebase); these just wire the shared confirm/toast dance around
// whichever mutate function a page hands in, so that dance (confirm ->
// call -> toast -> clear selection -> refetch) isn't rewritten three times.
//
// The typed-confirmation gate and the delete-preview's deactivate/delete
// split (root CLAUDE.md's rebuild task: "the confirmation dialog states
// which of the selected rows will be deleted and which will only be
// deactivated, with counts, before anything happens" + "bulk delete
// requires typing the number of records to confirm") both live here, once,
// so every resource's delete flow shows the same dialog shape.

// `previewFn(ids)` -> Promise<{ items: [{id, willDelete, willDeactivate, skip}] }>
// `deleteFn(ids, confirmedCount)` -> Promise<{ ok: number[], skipped: [...] }>
export async function confirmAndBulkDelete({
  ids,
  confirm,
  t,
  previewFn,
  deleteFn,
  toastOk,
  toastErr,
  onSettled,
  knownErrorCodes = [],
}) {
  if (ids.length === 0) return;

  let preview;
  try {
    preview = await previewFn(ids);
  } catch (err) {
    toastErr(err, knownErrorCodes);
    return;
  }

  const items = preview.items || [];
  const deleteCount = items.filter((i) => i.willDelete).length;
  const deactivateCount = items.filter((i) => i.willDeactivate).length;
  const skipCount = items.filter((i) => i.skip).length;

  const messageParts = [t('bulk.delete.previewDeletePart', { count: deleteCount })];
  if (deactivateCount > 0) {
    messageParts.push(t('bulk.delete.previewDeactivatePart', { count: deactivateCount }));
  }
  if (skipCount > 0) {
    messageParts.push(t('bulk.delete.previewSkipPart', { count: skipCount }));
  }

  const ok = await confirm({
    title: t('bulk.delete.confirmTitle', { count: ids.length }),
    message: messageParts.join(' '),
    confirmLabel: t('bulk.delete.confirmButton'),
    tone: 'danger',
    requireTypedConfirmation: {
      expected: ids.length,
      label: t('bulk.delete.typeCountLabel', { count: ids.length }),
    },
  });
  if (!ok) return;

  try {
    const result = await deleteFn(ids, ids.length);
    toastOk(t('bulk.actionDoneToast', { ok: result.ok.length, skipped: result.skipped.length }));
    onSettled?.();
  } catch (err) {
    toastErr(err, knownErrorCodes);
  }
}

// Deactivate/reactivate share this simpler shape — no preview, no typed
// count, just the ordinary confirm() every other danger action in this
// codebase already uses.
export async function confirmAndBulkAction({
  ids,
  confirm,
  t,
  titleId,
  messageId,
  confirmLabelId,
  actionFn,
  toastOk,
  toastErr,
  onSettled,
  knownErrorCodes = [],
}) {
  if (ids.length === 0) return;

  const ok = await confirm({
    title: t(titleId, { count: ids.length }),
    message: t(messageId, { count: ids.length }),
    confirmLabel: t(confirmLabelId),
    tone: 'danger',
  });
  if (!ok) return;

  try {
    const result = await actionFn(ids);
    toastOk(t('bulk.actionDoneToast', { ok: result.ok.length, skipped: result.skipped.length }));
    onSettled?.();
  } catch (err) {
    toastErr(err, knownErrorCodes);
  }
}
